import { isEmpty } from 'lodash-es'
import { computed, ref } from 'vue'

import { FilterCategoryKey } from '../components/MsResultListFilter/MsResultListFilter.enum'

import { SearchRequestKeys } from '../ts/mapper/CmsPropertyMapper.enum'
import { FilterId, Unit } from '../ts/service/realEstateService.enum'
import { Filters, SearchFilters } from '../ts/service/realEstateService.interface'
import { useMailAgentStore } from '../ts/store/mailAgentStoreModule/mailAgentStoreModule'
import { useResultListStore } from '../ts/store/resultListStoreModule/resultListStoreModule'
import { getEsrKey, updateSearchParams } from '../ts/utils/searchListUtils'
import { createRangeStr } from '../ts/utils/stringUtils'

export function useResultListFilter(initialSearchFilters: SearchFilters) {
    const { searchParams, resetPage } = useResultListStore()
    const filtersForSelectedEstate = ref(findFiltersForSelectedEstate(initialSearchFilters))

    const keyMap = ref({
        [FilterCategoryKey.priceRange]: { min: SearchRequestKeys.PRICE_MIN, max: SearchRequestKeys.PRICE_MAX },
        [FilterCategoryKey.areaRange]: { min: SearchRequestKeys.FLOOR_MIN, max: SearchRequestKeys.FLOOR_MAX },
        [FilterCategoryKey.roomRange]: { min: SearchRequestKeys.ROOM_MIN, max: SearchRequestKeys.ROOM_MAX },
    })

    const filterMinMaxValues = computed(() => ({
        [FilterCategoryKey.priceRange]: {
            min: searchParams[SearchRequestKeys.PRICE_MIN],
            max: searchParams[SearchRequestKeys.PRICE_MAX],
        },
        [FilterCategoryKey.areaRange]: {
            min: searchParams[SearchRequestKeys.FLOOR_MIN],
            max: searchParams[SearchRequestKeys.FLOOR_MAX],
        },
        [FilterCategoryKey.roomRange]: {
            min: searchParams[SearchRequestKeys.ROOM_MIN],
            max: searchParams[SearchRequestKeys.ROOM_MAX],
        },
    }))

    const hasValuesForFilterId = (filterId: FilterId) => !isEmpty(filtersForSelectedEstate.value[filterId].values)

    const hasToUpdateValue = <T>(newValue: T, currentValue: T): boolean => newValue !== currentValue

    const onUpdateMinMaxValue = (newValue: { min: number; max: number }, identifier: FilterCategoryKey) => {
        const keys = keyMap.value[identifier]
        const hasToUpdateMin = hasToUpdateValue(newValue.min, searchParams[keys.min])
        const hasToUpdateMax = hasToUpdateValue(newValue.max, searchParams[keys.max])
        if (hasToUpdateMin) {
            resetPage()
            updateSearchParams({ [keys.min]: newValue.min })
        }

        if (hasToUpdateMax) {
            resetPage()
            updateSearchParams({ [keys.max]: newValue.max })
        }

        if (hasToUpdateMin || hasToUpdateMax) {
            updateMailAgentStore(newValue, identifier)
        }
    }

    function updateMailAgentStore(value: { min: number; max: number }, identifier: FilterCategoryKey) {
        const unit = getUnitForIdentifier(identifier)
        useMailAgentStore().tags[identifier] = createRangeStr(value.min, value.max, unit)
    }

    function getUnitForIdentifier(identifier: FilterCategoryKey) {
        if (identifier === FilterCategoryKey.priceRange) {
            return Unit.EURO
        } else if (identifier === FilterCategoryKey.areaRange) {
            return Unit.SQUARE_METERS
        } else {
            return ` ${Unit.ROOMS}`
        }
    }

    function updateSearchFilters(newSearchFilters: SearchFilters) {
        filtersForSelectedEstate.value = findFiltersForSelectedEstate(newSearchFilters)
    }

    function findFiltersForSelectedEstate(searchFilters: SearchFilters): Filters {
        const etype = useResultListStore().searchParams.etype
        const estateType = getEsrKey(useResultListStore().searchParams.esr!)

        return searchFilters[estateType].estateTypes.find(
            (estateType: any): boolean => estateType.id === etype?.toString(),
        )?.filters as Filters
    }

    return {
        filtersForSelectedEstate,
        filterMinMaxValues,
        hasValuesForFilterId,
        onUpdateMinMaxValue,
        updateSearchFilters,
    }
}
